import { useState } from 'react';
import { expertApplications } from '@/mocks/dashboardData';

const statusColors: Record<string, { bg: string; color: string }> = {
  'Beklemede': { bg: 'rgba(245,158,11,0.15)', color: '#f59e0b' },
  'Onaylandı': { bg: 'rgba(16,185,129,0.15)', color: '#10b981' },
  'Reddedildi': { bg: 'rgba(239,68,68,0.15)', color: '#ef4444' },
  'İncelemede': { bg: 'rgba(255,140,66,0.15)', color: '#ff8c42' },
};

const cities = ['Tümü', 'İstanbul', 'Ankara', 'İzmir', 'Bursa', 'Antalya', 'Adana', 'Konya', 'Kayseri'];
const services = ['Tümü', 'Saç Kesimi', 'Sakal & Tıraş', 'Boya & Röfle', 'Bakım & Maske'];

interface DetailModalProps {
  expert: typeof expertApplications[0];
  onClose: () => void;
}

const DetailModal: React.FC<DetailModalProps> = ({ expert, onClose }) => {
  const [activeTab, setActiveTab] = useState('genel');
  const tabs = [
    { id: 'genel', label: 'Genel Bilgi' },
    { id: 'belgeler', label: 'Belgeler' },
    { id: 'referanslar', label: 'Referanslar' },
    { id: 'sosyal', label: 'Sosyal Medya' },
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.8)' }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-2xl"
        style={{ background: '#0d0d14', border: '1px solid rgba(255,77,77,0.3)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: 'rgba(255,77,77,0.2)' }}>
          <h2 className="text-base font-bold text-white">{expert.isim} — Başvuru Detayı</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white cursor-pointer">
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Profile */}
        <div className="px-6 py-4 flex items-center gap-4 border-b" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center text-white text-2xl font-bold flex-shrink-0"
            style={{ background: 'linear-gradient(135deg, #ff4d4d, #ff8c42)', border: '2px solid rgba(255,77,77,0.4)' }}
          >
            {expert.isim[0]}
          </div>
          <div>
            <div className="text-white font-semibold text-base">{expert.isim}</div>
            <div className="text-sm mt-0.5" style={{ color: '#9ca3af' }}>{expert.uzmanlik} · {expert.sehir}</div>
            <div className="flex items-center gap-1 mt-1">
              {Array.from({ length: 5 }).map((_, i) => (
                <i key={i} className={i < Math.round(expert.skor) ? 'ri-star-fill text-xs' : 'ri-star-line text-xs'} style={{ color: '#ff8c42' }}></i>
              ))}
              <span className="text-xs ml-1" style={{ color: '#ff8c42' }}>{expert.skor}</span>
            </div>
          </div>
          <div className="ml-auto">
            <span
              className="px-3 py-1 rounded-full text-xs font-medium"
              style={{ background: statusColors[expert.durum]?.bg, color: statusColors[expert.durum]?.color }}
            >
              {expert.durum}
            </span>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 px-6 pt-3">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className="px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer transition-all whitespace-nowrap"
              style={{
                background: activeTab === t.id ? 'linear-gradient(135deg, rgba(255,77,77,0.25), rgba(255,140,66,0.15))' : 'rgba(255,255,255,0.04)',
                color: activeTab === t.id ? '#ff6b47' : '#6b7280',
                border: activeTab === t.id ? '1px solid rgba(255,77,77,0.3)' : '1px solid transparent',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'genel' && (
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Şehir', value: expert.sehir },
                { label: 'Uzmanlık', value: expert.uzmanlik },
                { label: 'Deneyim', value: `${expert.deneyim} yıl` },
                { label: 'Başvuru Skoru', value: `${expert.skor}/5` },
                { label: 'Başvuru Tarihi', value: expert.tarih },
                { label: 'Telefon', value: expert.telefon },
                { label: 'E-posta', value: expert.email },
                { label: 'Durum', value: expert.durum },
              ].map((item) => (
                <div key={item.label} className="rounded-lg p-3" style={{ background: 'rgba(255,255,255,0.04)' }}>
                  <div className="text-xs mb-0.5" style={{ color: '#6b7280' }}>{item.label}</div>
                  <div className="text-sm text-white font-medium">{item.value}</div>
                </div>
              ))}
            </div>
          )}
          {activeTab === 'belgeler' && (
            <div className="space-y-2">
              {['Kimlik Belgesi', 'Uzmanlık Sertifikası', 'Oda Belgesi', 'Sağlık Raporu'].map((doc) => (
                <div key={doc} className="flex items-center justify-between p-3 rounded-lg" style={{ background: 'rgba(255,255,255,0.04)' }}>
                  <div className="flex items-center gap-2">
                    <i className="ri-file-text-line text-sm" style={{ color: '#ff8c42' }}></i>
                    <span className="text-sm text-white">{doc}</span>
                  </div>
                  <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(16,185,129,0.15)', color: '#10b981' }}>Yüklendi</span>
                </div>
              ))}
            </div>
          )}
          {activeTab === 'referanslar' && (
            <div className="space-y-2">
              {[
                { isim: 'Kuaför Salon A', konum: 'İstanbul', yil: '2020-2022' },
                { isim: 'Berber Ustası B', konum: 'Ankara', yil: '2018-2020' },
              ].map((ref) => (
                <div key={ref.isim} className="p-3 rounded-lg" style={{ background: 'rgba(255,255,255,0.04)' }}>
                  <div className="text-sm text-white font-medium">{ref.isim}</div>
                  <div className="text-xs mt-0.5" style={{ color: '#6b7280' }}>{ref.konum} · {ref.yil}</div>
                </div>
              ))}
            </div>
          )}
          {activeTab === 'sosyal' && (
            <div className="space-y-2">
              {[
                { platform: 'Instagram', handle: '@uzman_profil', icon: 'ri-instagram-line', color: '#e1306c' },
                { platform: 'TikTok', handle: '@uzmanprofil', icon: 'ri-tiktok-line', color: '#ffffff' },
              ].map((s) => (
                <div key={s.platform} className="flex items-center gap-3 p-3 rounded-lg" style={{ background: 'rgba(255,255,255,0.04)' }}>
                  <i className={`${s.icon} text-lg`} style={{ color: s.color }}></i>
                  <div>
                    <div className="text-sm text-white">{s.platform}</div>
                    <div className="text-xs" style={{ color: '#6b7280' }}>{s.handle}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2 px-6 pb-5">
          <button
            className="flex-1 py-2.5 rounded-lg text-sm font-semibold cursor-pointer transition-all hover:opacity-90 whitespace-nowrap"
            style={{ background: 'linear-gradient(135deg, #10b981, #059669)', color: '#fff' }}
          >
            <i className="ri-check-line mr-1.5"></i>Onayla
          </button>
          <button
            className="flex-1 py-2.5 rounded-lg text-sm font-semibold cursor-pointer transition-all hover:opacity-90 whitespace-nowrap"
            style={{ background: 'rgba(255,140,66,0.2)', color: '#ff8c42', border: '1px solid rgba(255,140,66,0.3)' }}
          >
            <i className="ri-eye-line mr-1.5"></i>İncelemeye Al
          </button>
          <button
            className="flex-1 py-2.5 rounded-lg text-sm font-semibold cursor-pointer transition-all hover:opacity-90 whitespace-nowrap"
            style={{ background: 'rgba(239,68,68,0.15)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.3)' }}
          >
            <i className="ri-close-line mr-1.5"></i>Reddet
          </button>
        </div>
      </div>
    </div>
  );
};

const ApplicationsTable: React.FC = () => {
  const [selectedCity, setSelectedCity] = useState('Tümü');
  const [selectedService, setSelectedService] = useState('Tümü');
  const [search, setSearch] = useState('');
  const [selectedExpert, setSelectedExpert] = useState<typeof expertApplications[0] | null>(null);
  const [statusFilter, setStatusFilter] = useState('Tümü');

  const statuses = ['Tümü', 'Beklemede', 'Onaylandı', 'İncelemede', 'Reddedildi'];

  const filtered = expertApplications.filter((e) => {
    const matchCity = selectedCity === 'Tümü' || e.sehir === selectedCity;
    const matchService = selectedService === 'Tümü' || e.uzmanlik === selectedService;
    const matchStatus = statusFilter === 'Tümü' || e.durum === statusFilter;
    const matchSearch = e.isim.toLowerCase().includes(search.toLowerCase());
    return matchCity && matchService && matchStatus && matchSearch;
  });

  return (
    <div>
      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative">
          <input
            type="text"
            placeholder="İsim ara..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 pr-3 py-1.5 rounded-lg text-sm text-white outline-none w-40"
            style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', fontSize: '13px' }}
          />
          <i className="ri-search-line absolute left-2.5 top-1/2 -translate-y-1/2 text-sm" style={{ color: '#6b7280' }}></i>
        </div>

        {[
          { label: 'Şehir', options: cities, value: selectedCity, onChange: setSelectedCity },
          { label: 'Hizmet', options: services, value: selectedService, onChange: setSelectedService },
          { label: 'Durum', options: statuses, value: statusFilter, onChange: setStatusFilter },
        ].map((filter) => (
          <select
            key={filter.label}
            value={filter.value}
            onChange={(e) => filter.onChange(e.target.value)}
            className="px-3 py-1.5 rounded-lg text-sm text-white outline-none cursor-pointer"
            style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', fontSize: '13px' }}
          >
            {filter.options.map((opt) => (
              <option key={opt} value={opt} style={{ background: '#0d0d14' }}>{opt === 'Tümü' ? `${filter.label}: Tümü` : opt}</option>
            ))}
          </select>
        ))}

        <div className="ml-auto text-xs self-center" style={{ color: '#6b7280' }}>
          {filtered.length} kayıt
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-xl" style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
        <table className="w-full text-sm">
          <thead>
            <tr style={{ background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
              {['İsim Soyisim', 'Şehir', 'Uzmanlık', 'Deneyim', 'Skor', 'Tarih', 'Durum', 'İşlem'].map((h) => (
                <th key={h} className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color: '#6b7280' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((expert, idx) => (
              <tr
                key={expert.id}
                onClick={() => setSelectedExpert(expert)}
                className="cursor-pointer transition-all duration-200"
                style={{
                  borderBottom: '1px solid rgba(255,255,255,0.04)',
                  background: idx % 2 === 0 ? 'rgba(255,255,255,0.01)' : 'transparent',
                }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLTableRowElement).style.background = 'rgba(255,77,77,0.06)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLTableRowElement).style.background = idx % 2 === 0 ? 'rgba(255,255,255,0.01)' : 'transparent'; }}
              >
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                      style={{ background: 'linear-gradient(135deg, #ff4d4d, #ff8c42)' }}
                    >
                      {expert.isim[0]}
                    </div>
                    <span className="text-white font-medium">{expert.isim}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-gray-300">{expert.sehir}</td>
                <td className="px-4 py-3 text-gray-300">{expert.uzmanlik}</td>
                <td className="px-4 py-3 text-gray-300">{expert.deneyim} yıl</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1">
                    <i className="ri-star-fill text-xs" style={{ color: '#ff8c42' }}></i>
                    <span className="text-white font-medium">{expert.skor}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-gray-400" style={{ fontSize: '12px' }}>{expert.tarih}</td>
                <td className="px-4 py-3">
                  <span
                    className="px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap"
                    style={{ background: statusColors[expert.durum]?.bg, color: statusColors[expert.durum]?.color }}
                  >
                    {expert.durum}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <button
                    className="px-2.5 py-1 rounded-lg text-xs cursor-pointer transition-all hover:opacity-80 whitespace-nowrap"
                    style={{ background: 'rgba(255,77,77,0.15)', color: '#ff6b47', border: '1px solid rgba(255,77,77,0.2)' }}
                    onClick={(e) => { e.stopPropagation(); setSelectedExpert(expert); }}
                  >
                    Detay
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedExpert && (
        <DetailModal expert={selectedExpert} onClose={() => setSelectedExpert(null)} />
      )}
    </div>
  );
};

export default ApplicationsTable;
